import { useCallback, useEffect, useState } from "react";
import { ShieldExclamationIcon } from "@heroicons/react/outline";
import Sidebar from "../../../components/Sidebar/Sidebar";
import { useToast } from "../../../components/Toastr/Toastr";
import DeleteModal from "../../../components/DeleteModal/DeleteModal";
import { apiV3 } from "../../../utils/helpers/api";
import { useAuth } from "../../../utils/auth/auth";
import {
  OfflineAccessStatus,
  OfflinePinRevealLogEntry,
  PinRotationCadence,
} from "../../../utils/types";

const CADENCES: PinRotationCadence[] = ["Daily", "Weekly", "Manual"];

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleString("en-AU") : "—";

/**
 * Offline Access — the shared PIN officers use to record entries on the
 * Officer Dashboard while the backend is unreachable.
 *
 * The PIN is emailed on every rotation, but a supervisor who missed the email
 * during an incident needs to be able to look it up. Every reveal is logged
 * server-side, because the PIN authorises writing into the security database.
 */
export default function OfflineAccessV3() {
  const auth = useAuth();
  const { showSuccessToast, showErrorToast } = useToast();

  const [status, setStatus] = useState<OfflineAccessStatus | null>(null);
  const [revealLog, setRevealLog] = useState<OfflinePinRevealLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [revealedPin, setRevealedPin] = useState<string | null>(null);
  const [rotateOpen, setRotateOpen] = useState(false);

  const [cadence, setCadence] = useState<PinRotationCadence>("Daily");
  const [rotationHour, setRotationHour] = useState(4);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState("");

  const isAdmin = auth.user?.role === 1 || auth.user?.role === 2;

  const load = useCallback(async () => {
    try {
      const [statusResponse, logResponse] = await Promise.all([
        apiV3.getOfflinePinStatus(),
        apiV3.getOfflinePinRevealLog(20),
      ]);
      const data: OfflineAccessStatus = statusResponse.data;
      setStatus(data);
      setCadence(data.rotationCadence);
      setRotationHour(data.rotationHour);
      setRecipients(data.emailRecipients ?? []);
      setRevealLog(logResponse.data?.data ?? []);
    } catch (error) {
      showErrorToast("Could not load offline access settings.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const reveal = async () => {
    try {
      const response = await apiV3.revealOfflinePin();
      setRevealedPin(response.data.pin);
      // Refresh the log so the admin sees their own reveal recorded.
      const logResponse = await apiV3.getOfflinePinRevealLog(20);
      setRevealLog(logResponse.data?.data ?? []);
    } catch (error) {
      showErrorToast("Could not reveal the PIN.");
    }
  };

  const rotate = async () => {
    setSaving(true);
    try {
      const response = await apiV3.rotateOfflinePin();
      setRevealedPin(response.data.pin);
      showSuccessToast("A new PIN has been generated and emailed.");
      await load();
    } catch (error) {
      showErrorToast("Could not rotate the PIN.");
    } finally {
      setSaving(false);
      setRotateOpen(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await apiV3.updateOfflinePinConfig({
        rotationCadence: cadence,
        rotationHour,
        emailRecipients: recipients,
      });
      showSuccessToast("Offline access settings saved.");
      await load();
    } catch (error) {
      showErrorToast("Could not save the settings.");
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = () => {
    const email = newRecipient.trim();
    if (!email.includes("@") || recipients.includes(email)) return;
    setRecipients([...recipients, email]);
    setNewRecipient("");
  };

  if (!isAdmin) {
    return (
      <Shell>
        <div className="p-8">
          <p className="text-sm text-gray-600">
            Only administrators can view offline access settings.
          </p>
        </div>
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell>
        <div className="p-8 text-sm text-gray-600">Loading…</div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
        <h1 className="text-2xl font-bold text-gray-900 flex flex-row items-center">
          <ShieldExclamationIcon className="h-6 w-6 mr-1.5" />
          Offline Access
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          The shared PIN security officers use to record entries on the Officer Dashboard when the
          backend is unreachable. Read it out to officers only during an outage — entries recorded
          this way carry no photo and no face match, and are flagged for review once the connection
          is restored.
        </p>

        {/* Current PIN */}
        <div className="mt-8 rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900">Current PIN</h2>

          {!status?.isSet ? (
            <p className="mt-3 text-sm text-gray-600">
              No PIN has been generated yet. One is created automatically on the next scheduled run,
              or generate one now.
            </p>
          ) : (
            <div className="mt-4 flex items-center gap-6">
              <span className="font-mono text-3xl tracking-[0.4em] text-gray-900">
                {revealedPin ?? "••••••"}
              </span>
              {!revealedPin && (
                <button
                  type="button"
                  onClick={reveal}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Reveal
                </button>
              )}
            </div>
          )}

          <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3 text-sm">
            <div>
              <dt className="text-gray-500">Version</dt>
              <dd className="text-gray-900">{status?.version ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Valid until</dt>
              <dd className="text-gray-900">{formatDate(status?.validUntil)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Last rotated</dt>
              <dd className="text-gray-900">{formatDate(status?.lastRotatedAt)}</dd>
            </div>
          </dl>

          <button
            type="button"
            onClick={() => setRotateOpen(true)}
            disabled={saving}
            className="mt-6 rounded-md bg-amber-500 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:bg-gray-300"
          >
            Rotate now
          </button>
          <p className="mt-2 text-xs text-gray-500">
            Rotating emails the new PIN to the list below. The previous PIN keeps working for one
            more rotation, so a tablet that has not checked in recently is not locked out
            mid-outage.
          </p>
        </div>

        {/* Rotation */}
        <div className="mt-8 rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900">Rotation</h2>

          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="cadence" className="block text-sm font-medium text-gray-700">
                Cadence
              </label>
              <select
                id="cadence"
                value={cadence}
                onChange={(e) => setCadence(e.target.value as PinRotationCadence)}
                className="mt-2 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              >
                {CADENCES.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="rotationHour" className="block text-sm font-medium text-gray-700">
                Hour (Melbourne time)
              </label>
              <input
                id="rotationHour"
                type="number"
                min={0}
                max={23}
                value={rotationHour}
                onChange={(e) => setRotationHour(parseInt(e.target.value, 10) || 0)}
                disabled={cadence === "Manual"}
                className="mt-2 block w-full rounded-md border-gray-300 py-2 px-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500 disabled:bg-gray-100"
              />
            </div>
          </div>
        </div>

        {/* Distribution list */}
        <div className="mt-8 rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900">Email recipients</h2>
          <p className="mt-1 text-sm text-gray-600">
            Everyone who should receive the new PIN when it rotates.
          </p>

          <ul className="mt-4 divide-y divide-gray-100">
            {recipients.length === 0 && (
              <li className="py-3 text-sm text-gray-500">
                Nobody is on the list — the PIN will only be visible here.
              </li>
            )}
            {recipients.map((email) => (
              <li key={email} className="flex items-center justify-between py-3 text-sm">
                <span className="text-gray-900">{email}</span>
                <button
                  type="button"
                  onClick={() => setRecipients(recipients.filter((r) => r !== email))}
                  className="text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex gap-3">
            <input
              type="email"
              value={newRecipient}
              placeholder="name@example.com"
              onChange={(e) => setNewRecipient(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addRecipient();
                }
              }}
              className="block w-full max-w-sm rounded-md border-gray-300 py-2 px-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
            <button
              type="button"
              onClick={addRecipient}
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Add
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={saveConfig}
          disabled={saving}
          className="mt-8 rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700 disabled:bg-gray-300"
        >
          {saving ? "Saving…" : "Save settings"}
        </button>

        {/* Reveal audit */}
        <div className="mt-10 rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-medium text-gray-900">Recent reveals</h2>
          {revealLog.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">Nobody has looked up the PIN yet.</p>
          ) : (
            <table className="mt-4 min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-2 pr-4 font-medium">When</th>
                  <th className="py-2 pr-4 font-medium">User</th>
                  <th className="py-2 font-medium">PIN version</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {revealLog.map((entry) => (
                  <tr key={entry.id}>
                    <td className="py-2 pr-4 text-gray-900">{formatDate(entry.revealedAt)}</td>
                    <td className="py-2 pr-4 text-gray-600">{entry.userId}</td>
                    <td className="py-2 text-gray-600">{entry.pinVersion}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <DeleteModal
          open={rotateOpen}
          setOpen={setRotateOpen}
          title="Rotate the offline PIN?"
          description="The current PIN stops being the primary one immediately. It keeps working for one more rotation so tablets that have not checked in recently are not locked out. The new PIN is emailed to the recipients above."
          actionButtonText="Rotate"
          action={rotate}
        />
      </div>
    </Shell>
  );
}

/**
 * Standard admin chrome. Every V3 page renders the sidebar itself rather than
 * inheriting it from a route layout, so this page has to as well.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Sidebar />
      <div className="md:pl-64 flex flex-col flex-1">
        <main className="flex-1">{children}</main>
      </div>
    </>
  );
}
